import { create } from 'zustand';

import { isDevicePushFlagged } from './push/device';

/**
 * Per-device presentation preferences of notifications (not account settings: a laptop may show desktop notifications
 * while the office PC does not). Remembered in localStorage.
 */
interface NotificationPrefsState {
  /** In-portal pop-ups (toasts) for new notifications while the tab is visible. */
  popups: boolean;
  /** Desktop notifications (Notification API) while the tab is in the background; needs the browser's permission. */
  desktop: boolean;
  setPopups: (value: boolean) => void;
  setDesktop: (value: boolean) => void;
}

const POPUPS_KEY = 'orbita.notifications.popups';
const DESKTOP_KEY = 'orbita.notifications.desktop';

function read(key: string, fallback: boolean): boolean {
  try {
    const value = localStorage.getItem(key);
    return value === null ? fallback : value === '1';
  } catch {
    return fallback;
  }
}

function write(key: string, value: boolean): void {
  try {
    localStorage.setItem(key, value ? '1' : '0');
  } catch {
    // Storage unavailable: the choice lasts for this session.
  }
}

export const useNotificationPrefs = create<NotificationPrefsState>()((set) => ({
  popups: read(POPUPS_KEY, true),
  desktop: read(DESKTOP_KEY, false),
  setPopups: (popups) => {
    write(POPUPS_KEY, popups);
    set({ popups });
  },
  setDesktop: (desktop) => {
    write(DESKTOP_KEY, desktop);
    set({ desktop });
  },
}));

/* ---------- Notification API ---------- */

export type DesktopPermission = NotificationPermission | 'unsupported';

export function desktopPermission(): DesktopPermission {
  return typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'unsupported';
}

/** Asks the browser for permission (must run in a user gesture); resolves to the resulting state. */
export async function requestDesktopPermission(): Promise<DesktopPermission> {
  if (desktopPermission() === 'unsupported') return 'unsupported';
  try {
    return await Notification.requestPermission();
  } catch {
    return desktopPermission();
  }
}

/**
 * Shows a desktop notification when enabled and permitted; clicking it focuses the tab and runs `onClick`. Not while web push
 * is on in this browser: the push message of the same event shows the notification (also with the portal closed), and two
 * notifications of one event would be one too many.
 */
export function showDesktopNotification(options: {
  title: string;
  body: string;
  tag: string;
  icon?: string | null;
  onClick: () => void;
}): boolean {
  if (!useNotificationPrefs.getState().desktop || desktopPermission() !== 'granted') return false;
  if (isDevicePushFlagged()) return false;
  try {
    const notification = new Notification(options.title, {
      body: options.body,
      tag: options.tag,
      icon: options.icon ?? '/favicon.svg',
    });
    notification.onclick = () => {
      window.focus();
      options.onClick();
      notification.close();
    };
    return true;
  } catch {
    // Some browsers (Android Chrome) only allow notifications from a service worker.
    return false;
  }
}

/* ---------- navigation from outside React (toasts, desktop notifications) ---------- */

let navigator: (path: string) => void = (path) => window.location.assign(path);

/** The bell registers the router's navigate function, so toast and desktop clicks stay inside the SPA. */
export function setNotificationNavigator(navigate: (path: string) => void): () => void {
  navigator = navigate;
  return () => {
    if (navigator === navigate) navigator = (path) => window.location.assign(path);
  };
}

export function navigateTo(path: string): void {
  navigator(path);
}
